import React, { useCallback, useMemo } from 'react';
import { FileRejection, useDropzone } from 'react-dropzone';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import Box from '@mui/material/Box';
import NumberUtils from './../../utils/numberUtils';
import './TwDropzone.scss';

export type TwDropzoneFiletypes =
  | 'image/jpeg'
  | 'image/png'
  | 'image/heic'
  | 'application/pdf'
  | 'text/csv';

export interface TwDropzoneProps {
  /**
   * List of file types allowed to be uploaded
   */
  acceptedFiletypes?: TwDropzoneFiletypes[];
  /**
   * If true, drag and drop of files is allowed.
   * Otherwise only upload by click is allowed.
   */
  allowDragging?: boolean;
  /**
   * Action to perform after uploading files.
   */
  onFileUploaded: (files: File[]) => void;
  /**
   * Action to perform if errors.
   */
  onErrors: (errors: string[]) => void;
  /**
   * Number of files allowed to upload.
   * 0 means no restriction.
   */
  maxFiles?: number;
  /**
   * File max size
   */
  maxSize?: number;
  /**
   * Container className
   */
  containerClassName?: string;
  /**
   * Text displayed after icon
   */
  text?: string;
  /**
   * Highlighted text displayed after text prop
   */
  highlightedText?: string;
  /**
   * Secondary text displayed below text prop
   */
  secondaryText?: string;
}

const baseStyle = {
  flex: 1,
  display: 'flex',
  height: '60px',
  flexDirection: 'column' as const,
  alignItems: 'center',
  justifyContent: 'center',
  padding: '20px',
  borderWidth: 1,
  borderRadius: 4,
  borderColor: '#D1D5DB',
  borderStyle: 'dashed',
  backgroundColor: '#F9FAFB',
  outline: 'none',
  transition: 'border .24s ease-in-out',
  fontSize: '14px',
  cursor: 'pointer'
};

const TwDropzone: React.FC<TwDropzoneProps> = ({
  acceptedFiletypes,
  allowDragging,
  maxFiles,
  maxSize,
  containerClassName,
  text,
  highlightedText,
  secondaryText,
  onFileUploaded,
  onErrors
}): JSX.Element => {
  const onDrop = useCallback((acceptedFiles: File[]) => {
    onErrors([]);
    onFileUploaded(acceptedFiles);
  }, []);

  const getFileTypesAccepted = (acceptedFileTypes: TwDropzoneFiletypes[]): string => {
    const fileTypes = acceptedFileTypes.map((fileType: TwDropzoneFiletypes) =>
      fileType.split('/')[1].toUpperCase()
    );

    if (fileTypes.length > 1) {
      const lastFileType = fileTypes.pop();
      return `${fileTypes.join(', ')} and ${lastFileType}`;
    }

    return fileTypes[0];
  };

  const onDropRejected = useCallback((fileRejections: FileRejection[]) => {
    const errors: string[] = [];

    fileRejections.map((rejection) => {
      rejection.errors.map((error) => {
        if (error.code === 'file-too-large') {
          errors.push(`Error: file size is greater than ${NumberUtils.getFileSize(maxSize || 0)}`);
        }

        if (error.code === 'file-invalid-type') {
          if (acceptedFiletypes) {
            errors.push(
              `Error: Only ${getFileTypesAccepted(acceptedFiletypes)} file type${
                acceptedFiletypes?.length > 1 ? 's' : ''
              } ${acceptedFiletypes?.length > 1 ? 'are' : 'is'} supported`
            );
          } else {
            errors.push('Error: selected file type is not supported');
          }
        }

        if (error.code === 'too-many-files') {
          const error = `Error: Max file count is ${maxFiles}`;
          const errorIsAlreadyOnTheList = errors.find((e) => e === error);
          if (!errorIsAlreadyOnTheList) errors.push(error);
        }
      });
    });

    onErrors(errors);
  }, []);

  const { getRootProps, getInputProps } = useDropzone({
    disabled: !allowDragging,
    accept: acceptedFiletypes,
    maxFiles: maxFiles,
    maxSize: maxSize,
    onDrop,
    onDropRejected
  });

  const style = useMemo(
    () => ({
      ...baseStyle
    }),
    []
  );

  return (
    <Box component="section" className={`drop-zone__container tid-dropzone`}>
      <div {...getRootProps({ style })} className={`tid-dropzone-area ${containerClassName}`}>
        <input {...getInputProps()} />
        <p
          className="drop-zone__text tid-dropzone-helptext"
          style={{ justifyContent: text && highlightedText ? 'flex-start' : 'center' }}
        >
          <CloudUploadIcon
            className="drop-zone__icon"
            sx={{ marginRight: highlightedText ? '10px' : '0' }}
          />
          {text}
          {!!highlightedText && (
            <span className="drop-zone__highlited-text">{highlightedText}</span>
          )}
        </p>
        {!!secondaryText && (
          <p className="drop-zone__text drop-zone__second-text tid-dropzone-second_helptext">
            {secondaryText}
          </p>
        )}
      </div>
    </Box>
  );
};

export default TwDropzone;

TwDropzone.defaultProps = {
  acceptedFiletypes: ['application/pdf', 'image/png', 'image/jpeg', 'image/heic'],
  allowDragging: true,
  maxFiles: 0,
  text: 'Drop file here or',
  highlightedText: 'click to upload'
};
